import { useState } from "react";
import {
  Microphone, Camera, Bell, Images, ShieldCheck, CheckCircle, XCircle, CircleNotch,
} from "@phosphor-icons/react";
import {
  requestMicrophone, requestCamera, requestNotifications,
  notificationsSupported, isIOS, isStandalone, logPermissionEvent,
} from "@/lib/devicePermissions";

const ONBOARD_KEY = "jared_onboarding_v1";

export function shouldShowJaredOnboarding() {
  try {
    if (localStorage.getItem(ONBOARD_KEY) === "done") return false;
  } catch { /* noop */ }
  const touch = "ontouchstart" in window || navigator.maxTouchPoints > 0;
  return isStandalone() || touch;
}

const PERMS = [
  { key: "microphone", icon: Microphone, title: "Micrófono", req: true,
    desc: "Para conversar por voz con Jared y darle comandos hablados." },
  { key: "camera", icon: Camera, title: "Cámara", req: true,
    desc: "Para inspecciones, captura de evidencias y análisis visual de hallazgos." },
  { key: "notifications", icon: Bell, title: "Notificaciones", req: false,
    desc: "Para alertas, tareas asignadas y seguimiento de acciones." },
  { key: "files", icon: Images, title: "Archivos y Fotos", req: false,
    desc: "Para adjuntar imágenes, documentos y evidencias (el selector aparece al adjuntar)." },
];

export default function JaredOnboarding({ onClose }) {
  const [status, setStatus] = useState({}); // key -> 'granted'|'denied'|'default'|'unsupported'|'loading'
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);

  const finish = (skipped = false) => {
    try { localStorage.setItem(ONBOARD_KEY, "done"); } catch { /* noop */ }
    logPermissionEvent("onboarding", skipped ? "skipped" : "completed", { results: JSON.stringify(status) });
    onClose?.();
  };

  const grantAll = async () => {
    setRunning(true);
    const next = { ...status };
    // Micrófono (obligatorio)
    next.microphone = "loading"; setStatus({ ...next });
    next.microphone = await requestMicrophone(); setStatus({ ...next });
    // Cámara (obligatorio)
    next.camera = "loading"; setStatus({ ...next });
    next.camera = await requestCamera(); setStatus({ ...next });
    // Notificaciones (si el dispositivo lo soporta)
    if (notificationsSupported()) {
      next.notifications = "loading"; setStatus({ ...next });
      next.notifications = await requestNotifications(); setStatus({ ...next });
    } else {
      next.notifications = "unsupported"; setStatus({ ...next });
    }
    next.files = "info"; setStatus({ ...next });
    setRunning(false);
    setDone(true);
  };

  const chip = (key) => {
    const s = status[key];
    if (!s) return null;
    if (s === "loading") return <CircleNotch size={18} className="animate-spin text-slate-300" />;
    if (s === "granted") return <CheckCircle size={18} weight="fill" className="text-emerald-400" />;
    if (s === "denied") return <XCircle size={18} weight="fill" className="text-red-400" />;
    if (s === "unsupported") return <span className="text-[10px] text-slate-400">No disponible</span>;
    if (s === "info") return <span className="text-[10px] text-slate-400">Al adjuntar</span>;
    return <span className="text-[10px] text-amber-300">Pendiente</span>;
  };

  const micDenied = status.microphone === "denied";

  return (
    <div className="fixed inset-0 z-[60] bg-[#0a0a0a]/95 backdrop-blur-sm flex items-center justify-center p-4"
         data-testid="jared-onboarding">
      <div className="w-full max-w-md rounded-2xl bg-[#111318] border border-zinc-800 text-white overflow-hidden shadow-2xl">
        <div className="px-6 pt-6 pb-4 text-center border-b border-zinc-800/70">
          <div className="mx-auto w-14 h-14 rounded-full flex items-center justify-center mb-3"
               style={{ background: "rgba(227,30,36,0.15)" }}>
            <ShieldCheck size={30} weight="duotone" style={{ color: "var(--brand,#E31E24)" }} />
          </div>
          <h2 className="text-xl font-bold">Configura a Jared</h2>
          <p className="text-sm text-slate-400 mt-1">
            Para que Jared funcione bien en tu dispositivo, activa estos permisos.
          </p>
        </div>

        <div className="px-5 py-4 space-y-3 max-h-[52vh] overflow-y-auto">
          {PERMS.map(({ key, icon: Icon, title, desc, req }) => (
            <div key={key} className="flex items-start gap-3 rounded-xl bg-zinc-900/60 border border-zinc-800 p-3"
                 data-testid={`onboarding-perm-${key}`}>
              <div className="w-9 h-9 rounded-lg bg-zinc-800 flex items-center justify-center shrink-0">
                <Icon size={20} weight="duotone" style={{ color: "var(--brand,#E31E24)" }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold">{title}</span>
                  {req && <span className="text-[9px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-300 font-semibold">Obligatorio</span>}
                </div>
                <p className="text-xs text-slate-400 mt-0.5 leading-snug">{desc}</p>
              </div>
              <div className="shrink-0 self-center w-16 flex justify-end">{chip(key)}</div>
            </div>
          ))}

          {isIOS() && !isStandalone() && (
            <p className="text-[11px] text-amber-300/90 bg-amber-500/10 rounded-lg px-3 py-2">
              En iPhone, agrega la app a la pantalla de inicio (Compartir → “Añadir a inicio”) para
              recibir notificaciones.
            </p>
          )}
          {micDenied && (
            <p className="text-[11px] text-red-300 bg-red-500/10 rounded-lg px-3 py-2" data-testid="onboarding-mic-denied">
              Rechazaste el micrófono: Jared no podrá escuchar comandos de voz. Puedes activarlo luego
              desde los ajustes de tu dispositivo.
            </p>
          )}
        </div>

        <div className="px-5 py-4 border-t border-zinc-800/70 flex flex-col gap-2">
          {!done ? (
            <button onClick={grantAll} disabled={running} data-testid="onboarding-grant-btn"
                    className="w-full py-3 rounded-xl font-semibold text-white disabled:opacity-60 transition-opacity hover:opacity-90"
                    style={{ background: "var(--brand,#E31E24)" }}>
              {running ? "Solicitando permisos…" : "Activar permisos"}
            </button>
          ) : (
            <button onClick={() => finish(false)} data-testid="onboarding-continue-btn"
                    className="w-full py-3 rounded-xl font-semibold text-white transition-opacity hover:opacity-90"
                    style={{ background: "var(--brand,#E31E24)" }}>
              Continuar
            </button>
          )}
          <button onClick={() => finish(true)} data-testid="onboarding-skip-btn"
                  className="w-full py-2 rounded-xl text-sm text-slate-400 hover:text-white transition-colors">
            Ahora no
          </button>
        </div>
      </div>
    </div>
  );
}
